import { createRoot, type Root } from 'react-dom/client';
import { LandingNav } from '@/components/landing/LandingNav';
import { SeoLinkTree, type SeoLinkNode } from '@/components/seo/seo-link-tree';

let navRoot: Root | null = null;
let linkTreeRoot: Root | null = null;

function mountNav() {
  const navMount = document.getElementById('site-nav');
  if (!navMount) return;

  if (!navRoot) {
    navRoot = createRoot(navMount);
  }
  navRoot.render(<LandingNav />);
}

function mountLinkTree() {
  const treeEl = document.getElementById('seo-link-tree-root');
  const treeDataEl = document.getElementById('seo-link-tree-data');
  if (!treeEl || !treeDataEl?.textContent) return;

  try {
    const parsed = JSON.parse(treeDataEl.textContent) as SeoLinkNode | SeoLinkNode[];
    const nodes = Array.isArray(parsed) ? parsed : [parsed];
    if (linkTreeRoot) {
      linkTreeRoot.unmount();
      linkTreeRoot = null;
    }
    linkTreeRoot = createRoot(treeEl);
    linkTreeRoot.render(<SeoLinkTree nodes={nodes} />);
  } catch {
    // Fall back to static noscript links if JSON is invalid.
  }
}

function mountFooterYear() {
  const yearEl = document.getElementById('footer-year');
  if (yearEl) {
    yearEl.textContent = String(new Date().getFullYear());
  }
}

/** Mount or refresh SEO page interactive regions (nav, sidebar tree, footer year). */
export function mountSeoPage({ remountNav = false }: { remountNav?: boolean } = {}) {
  if (remountNav && navRoot) {
    try {
      navRoot.unmount();
    } catch {
      // ignore
    }
    navRoot = null;
  }
  if (remountNav || !navRoot) {
    mountNav();
  }
  mountLinkTree();
  mountFooterYear();
  mountContactForm();
}

function mountContactForm() {
  const form = document.getElementById('contact-form') as HTMLFormElement | null;
  const btn = document.getElementById('form-submit-btn') as HTMLButtonElement | null;
  if (!form || !btn || form.dataset.bound === '1') return;
  form.dataset.bound = '1';

  const btnLabel = btn.querySelector('.form-submit-label');
  const successMsg = document.getElementById('form-success-msg');

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const originalText = btnLabel?.textContent || btn.textContent || 'Send message';
    if (btnLabel) btnLabel.textContent = 'Sending…';
    else btn.textContent = 'Sending…';
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;

    try {
      const formData = new FormData(form);
      const payload = {
        name: formData.get('name'),
        email: formData.get('email'),
        phone: formData.get('phone'),
        message: formData.get('message'),
      };
      const res = await fetch(
        'https://us-central1-thinkincodeza.cloudfunctions.net/sendContactEmail',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        },
      );
      if (!res.ok) {
        let details = '';
        try {
          const j = await res.json();
          details = j?.error || j?.details || '';
        } catch {
          /* ignore */
        }
        throw new Error(details || 'server error');
      }
      form.querySelectorAll('input:not([type=hidden]), textarea').forEach((el) => {
        (el as HTMLInputElement | HTMLTextAreaElement).value = '';
      });
      if (successMsg) successMsg.style.display = 'block';
      btn.classList.remove('is-loading');
      if (btnLabel) btnLabel.textContent = 'Sent!';
      else btn.textContent = 'Sent!';
      setTimeout(() => {
        if (btnLabel) btnLabel.textContent = originalText;
        else btn.textContent = originalText;
        btn.disabled = false;
        btn.setAttribute('aria-busy', 'false');
        if (successMsg) successMsg.style.display = 'none';
      }, 5000);
    } catch (err) {
      btn.classList.remove('is-loading');
      btn.setAttribute('aria-busy', 'false');
      if (btnLabel) btnLabel.textContent = originalText;
      else btn.textContent = originalText;
      btn.disabled = false;
      const message = err instanceof Error ? err.message : '';
      alert(
        `Something went wrong sending your message. Please email us directly at hello@thinkincode.co.za` +
          (message ? `\n\nDetails: ${message}` : ''),
      );
    }
  });
}
